import { initializeApi, resetApi, getBaseUrl, getAuthCredentials, apiPost } from './api';
import type { User, AuthCredentials } from '@/types';

const AUTH_STORAGE_KEY = 'op_dashboard_auth';

export interface StoredAuth {
  credentials: string;
  apiToken: string;
  user: User;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  instanceUrl: string | null;
  credentials: string | null;
}

// Login prin serverul de autentificare (verificare directă în DB)
export async function login(credentials: AuthCredentials): Promise<User> {
  const { username, password } = credentials;

  // Apelăm serverul de autentificare
  const response = await fetch('/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw {
      message: data.message || 'Eroare de autentificare',
      status: response.status,
    };
  }

  // Creăm credențialele Basic Auth cu API token pentru OpenProject API
  const basicAuth = btoa(`apikey:${data.apiToken}`);

  // Inițializăm API-ul pentru OpenProject
  initializeApi('', basicAuth);

  // Construim obiectul user
  const user: User = {
    id: data.user.id,
    login: data.user.login,
    firstName: data.user.firstName,
    lastName: data.user.lastName,
    email: data.user.email,
    status: data.user.status,
    admin: data.user.admin,
  };

  // Salvăm în localStorage
  const storedAuth: StoredAuth = {
    credentials: basicAuth,
    apiToken: data.apiToken,
    user: user,
  };
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(storedAuth));

  return user;
}

// Invalidare sesiune pe server (best-effort).
// Trebuie apelată ÎNAINTE de logout()/resetApi(), cât timp credențialele
// sunt încă active, pentru ca antetul Authorization să fie trimis corect.
export async function logoutServer(): Promise<void> {
  try {
    await apiPost('/auth/logout', {}, { timeout: 10000 });
  } catch {
    // Best-effort: dacă serverul nu răspunde sau sesiunea e deja invalidă,
    // continuăm oricum cu logout-ul local.
  }
}

// Deconectare (curățare stare locală)
export function logout(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  resetApi();
}

// Restaurare sesiune din localStorage
export async function restoreSession(): Promise<User | null> {
  const stored = localStorage.getItem(AUTH_STORAGE_KEY);

  if (!stored) {
    return null;
  }

  try {
    const { credentials, user }: StoredAuth = JSON.parse(stored);

    // Re-inițializăm API-ul
    initializeApi('', credentials);

    // Verificăm dacă sesiunea este încă validă făcând un request simplu
    const response = await fetch('/auth/data/users/me', {
      headers: {
        'Authorization': `Basic ${credentials}`,
      },
    });

    if (!response.ok) {
      throw new Error('Session invalid');
    }

    return user;
  } catch {
    // Sesiunea nu mai este validă
    localStorage.removeItem(AUTH_STORAGE_KEY);
    resetApi();
    return null;
  }
}

// Obținere stare autentificare curentă
export function getStoredAuth(): StoredAuth | null {
  const stored = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

// Verificare dacă utilizatorul este autentificat
export function isAuthenticated(): boolean {
  return getStoredAuth() !== null;
}

// Obținere utilizator curent din storage
export function getCurrentUser(): User | null {
  const stored = getStoredAuth();
  return stored?.user || null;
}

// Obținere stare completă pentru context
export function getAuthState(): AuthState {
  const stored = getStoredAuth();

  return {
    isAuthenticated: stored !== null,
    user: stored?.user || null,
    instanceUrl: '',
    credentials: stored?.credentials || null,
  };
}

// Export funcții utilitare
export { getBaseUrl, getAuthCredentials };
